/* global GM_STATE */
import { useEffect, useState } from 'preact/hooks'
import { emitterProps, globalEmitter } from 'helpers/emitter-event'
import Icon from 'snippets/icon/icon'

const WAREHOUSE_KEY_MAP = {
  Brisbane: 'brisbane_stock_json',
  Melbourne: 'melbourne_stock_json'
}

function getMetafieldKey(warehouseName) {
  return WAREHOUSE_KEY_MAP[warehouseName] || null
}

function parseStockJson(raw) {
  try {
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw
    const entries = Object.values(parsed)[0]
    if (!Array.isArray(entries)) return null
    return Object.assign({}, ...entries)
  } catch {
    return null
  }
}

function ProductShipping() {
  const t = window.translate.location
  const notifyMeWhenAvailable = GM_STATE.apps.backInStock.notifyMeWhenAvailable
  const klaviyoNotifyPopupId = GM_STATE.apps.backInStock.klaviyoNotifyPopupId
  const [warehouse, setWarehouse] = useState(() => localStorage.getItem('location_warehouse_name') || null)
  const [currentVariant, setCurrentVariant] = useState(() => window.productState.initProduct.initVariant)
  const [postcode, setPostcode] = useState(() => localStorage.getItem('location_postcode') || null)

  useEffect(() => {
    const onPostcodeUpdated = (e) => {
      setWarehouse(e.detail.warehouse)
      setPostcode(e.detail.postcode)
    }
    const onVariantUpdated = (variant) => setCurrentVariant(variant)

    window.addEventListener('postcode:updated', onPostcodeUpdated)
    globalEmitter.on(emitterProps.product.updateVariant, onVariantUpdated)

    return () => {
      window.removeEventListener('postcode:updated', onPostcodeUpdated)
      globalEmitter.off(emitterProps.product.updateVariant, onVariantUpdated)
    }
  }, [])

  const openNotifyModal = () => {
    window._klOnsite = window._klOnsite || []
    window._klOnsite.push(['openForm', klaviyoNotifyPopupId])
  }

  const lowStockThreshold = window.productState.lowStockThreshold
  const lowStockMessage = window.productState.lowStockMessage
  const lowStockMessageOverride = window.productState.lowStockMessageOverride
  const metafieldKey = warehouse ? getMetafieldKey(warehouse) : null
  const rawStockJson = metafieldKey ? currentVariant.metafields?.[metafieldKey] : null
  const stockData = rawStockJson ? parseStockJson(rawStockJson) : null
  const isOutOfStock = stockData?.stockstatusmessage === 'soldout'
  const isPreOrder = stockData?.stockstatusmessage === 'preorder'
  const isLowStock = stockData?.stockstatusmessage === 'instock' && stockData?.onhandstock > 0 && stockData?.onhandstock <= lowStockThreshold
  const isInStock = stockData?.stockstatusmessage === 'instock' && !isLowStock
  const shippingToMessage = postcode ? t.product.shipping_to.replace('{postcode}', postcode) : ''

  const buttonAriaLabel = postcode
    ? `${t.cta.delivering_to} ${postcode}`
    : t.cta.set_postcode

  return (
    <>
      <button
        type="button"
        className="flex items-start justify-between w-full gap-2 text-xs font-normal text-current transition-all duration-200 ease-in-out bg-transparent outline-none cursor-pointer lg:text-sm group/btn hover:opacity-80 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-current js-modal-dialog-trigger"
        id="PostcodeSelector"
        aria-label={buttonAriaLabel}
        aria-haspopup="dialog"
        aria-controls="PostcodeSelectorModal"
        aria-expanded="false"
        data-postcode-button
      >
        <span className="flex items-start gap-2">
          {isOutOfStock ? (
            <>
              <span className="flex items-center justify-center flex-shrink-0 relative top-[1px]" aria-hidden="true">
                <Icon name="icon-info" viewBox="0 0 20 20" className="w-[20px] h-[20px]" />
              </span>
              <span className="font-[550]">{t.product.out_of_stock}</span>
            </>
          ) : (
            <>
              <span className="flex items-center justify-center flex-shrink-0 relative top-[1px]" aria-hidden="true">
                <Icon name="icon-truck-thin" viewBox="0 0 20 20" className="w-[20px] h-[20px]" />
              </span>
              {postcode ? (
                <span className="font-[550]" data-postcode-value>{shippingToMessage}</span>
              ) : (
                <span className="flex flex-col items-start">
                  <span className="font-[550]">{t.cta.set_postcode}</span>
                  {!warehouse && (
                    <span className="text-xs leading-relaxed text-grey-400">
                      {t.callout.choose_subtext}
                    </span>
                  )}
                </span>
              )}
            </>
          )}
        </span>
        <span className="flex-shrink-0 relative top-[2px]" aria-hidden="true">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 16 16" className="icon w-[20px] h-[20px]">
            <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.4" d="m5.75 3.5 4.5 4.5-4.5 4.5" />
          </svg>
        </span>
      </button>

      <div role="status" aria-live="polite" aria-atomic="false">
        {warehouse && (
          <div className="mt-3">
            <div className="text-sm pl-7">
              {/* In Stock UI */}
              {isInStock && (
                <div data-product-status>
                  <span className="flex relative items-center gap-1.5">
                    <span className="absolute flex-shrink-0 inline-block w-[10px] h-[10px] rounded-full -left-[22px] top-[7px] bg-success-dark" aria-hidden="true"></span>
                    <span className="font-[550]">{t.product.in_stock}</span>
                  </span>
                  <p className="text-grey-400">{t.product.dispatch_message} <span className="font-[550]">{t.product.dispatch_duration}</span></p>
                </div>
              )}

              {/* Low Stock UI */}
              {isLowStock && (
                <div data-product-status>
                  <span className="flex relative items-center gap-1.5">
                    <span className="absolute flex-shrink-0 inline-block w-[10px] h-[10px] rounded-full -left-[22px] top-[7px] bg-warning-content" aria-hidden="true"></span>
                    <span className="font-[550]">Only {stockData.onhandstock} left</span>
                  </span>
                  <p className="text-grey-400">{lowStockMessageOverride !== null ? lowStockMessageOverride : lowStockMessage}</p>
                </div>
              )}

              {/* Out of Stock UI */}
              {isOutOfStock && (
                <div data-product-status>
                  <span className="relative text-grey-400">
                    <span className="absolute flex-shrink-0 inline-block w-[10px] h-[10px] rounded-full -left-[22px] top-1 bg-error-content" aria-hidden="true"></span>
                    {t.product.out_of_stock_message}
                  </span>
                </div>
              )}
            </div>
            {isOutOfStock ? (
              <button
                className="w-full mt-4 button-primary klaviyo-bis-trigger"
                title={notifyMeWhenAvailable}
                klaviyo-bis-trigger
              >
                {notifyMeWhenAvailable}
              </button>
            ) : (
              <dl className="flex flex-col gap-1 pt-4 mt-4 text-sm border-t border-light">
                <div className="flex justify-between">
                  <dt>{t.product.standard_shipping}</dt>
                  <dd className='font-[550]'>$0.00</dd>
                </div>
                <div className="flex justify-between">
                  <dt>{t.product.express_shipping}</dt>
                  <dd className='font-[550]'>$0.00</dd>
                </div>
              </dl>
            )}
          </div>
        )}
      </div>
    </>
  )
}

export default ProductShipping
